import * as React from 'react';
import propz from 'propz';
import { AppUser } from 'Src/views/App/App';
import { resolveAgeNamingFromContext, formatSingleAgeLabel } from 'Src/helpers/ageGroups';
import { AGE_GROUPS_SORTED } from 'Src/consts/school';
import { AfterSchoolClubProgramme } from 'Src/models/clubProgramme';
import { SchoolForm } from 'Src/models/form';

type Props = {
  user: AppUser;
  forms: SchoolForm[];
  programme?: Partial<AfterSchoolClubProgramme>;
  audienceAges: number[];
  audienceGenders: Array<'MALE' | 'FEMALE'>;
  audienceInvitations: 'PARENT' | 'STUDENT' | 'BOTH';
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  agesError?: string;
  gendersError?: string;
  invitationsError?: string;
  isAgesInvalid: boolean;
  isGendersInvalid: boolean;
  isInvitationsInvalid: boolean;
  disabled?: boolean;
};

export const ClubProgrammeAudienceFields: React.FC<Props> = ({
  user,
  forms,
  programme,
  audienceAges,
  audienceGenders,
  audienceInvitations,
  setFieldValue,
  agesError,
  gendersError,
  invitationsError,
  isAgesInvalid,
  isGendersInvalid,
  isInvitationsInvalid,
  disabled = false
}) => {
  const naming = resolveAgeNamingFromContext(user, programme);
  const sortOrder: string[] = propz.get(AGE_GROUPS_SORTED, [naming], []);
  const ageOptions = Array.from(
    new Set(
      (Array.isArray(forms) ? forms : [])
        .map((form: any) => Number(propz.get(form, ['age'], NaN)))
        .filter((age: number) => Number.isFinite(age))
        .map((age: number) => Math.trunc(age))
    )
  )
    .map(age => ({ age, label: formatSingleAgeLabel(age, naming) }))
    .sort((a, b) => {
      const aIndex = sortOrder.indexOf(a.label);
      const bIndex = sortOrder.indexOf(b.label);
      const safeA = aIndex === -1 ? 9999 : aIndex;
      const safeB = bIndex === -1 ? 9999 : bIndex;
      return safeA === safeB ? a.age - b.age : safeA - safeB;
    });

  const toggleAge = (age: number) => {
    if (disabled) return;
    const next = audienceAges.includes(age) ? audienceAges.filter(item => item !== age) : [...audienceAges, age];
    setFieldValue('audienceAges', next);
  };

  const toggleGender = (gender: 'MALE' | 'FEMALE') => {
    if (disabled) return;
    const next = audienceGenders.includes(gender)
      ? audienceGenders.filter(item => item !== gender)
      : [...audienceGenders, gender];
    setFieldValue('audienceGenders', next);
  };

  return (
    <div className={`cpEditorAudience ${disabled ? 'isDisabled' : ''}`}>
      <div className="cpEditorField cpEditorFieldWide">
        <div className="cpEditorLabelRow">
          <label>
            Age groups <span>*</span>
          </label>
          <button
            type="button"
            className="cpEditorTextAction"
            disabled={disabled}
            onClick={() =>
              setFieldValue(
                'audienceAges',
                audienceAges.length === ageOptions.length ? [] : ageOptions.map(option => option.age)
              )
            }
          >
            {audienceAges.length === ageOptions.length && ageOptions.length > 0 ? 'Clear all' : 'Select all'}
          </button>
        </div>
        <div className="cpEditorChips cpEditorChipsWrap" role="group" aria-label="Age groups">
          {ageOptions.map(option => {
            const selected = audienceAges.includes(option.age);
            return (
              <button
                key={option.age}
                type="button"
                className={`cpEditorChip ${selected ? 'isSelected' : ''}`}
                aria-pressed={selected}
                disabled={disabled}
                onClick={() => toggleAge(option.age)}
              >
                {option.label}
              </button>
            );
          })}
        </div>
        {!ageOptions.length ? <p className="cpEditorHint">No school age groups are available.</p> : null}
        {isAgesInvalid ? <div className="cpEditorError">{agesError}</div> : null}
      </div>

      <div className="cpEditorField">
        <label>
          Gender <span>*</span>
        </label>
        <div className="cpEditorChips" role="group" aria-label="Gender">
          {[
            { value: 'MALE' as const, label: 'Male' },
            { value: 'FEMALE' as const, label: 'Female' }
          ].map(option => {
            const selected = audienceGenders.includes(option.value);
            return (
              <button
                key={option.value}
                type="button"
                className={`cpEditorChip ${selected ? 'isSelected' : ''}`}
                aria-pressed={selected}
                disabled={disabled}
                onClick={() => toggleGender(option.value)}
              >
                {option.label}
              </button>
            );
          })}
        </div>
        {isGendersInvalid ? <div className="cpEditorError">{gendersError}</div> : null}
      </div>

      <div className="cpEditorField">
        <label>
          Send notifications to <span>*</span>
        </label>
        <div className="cpEditorChips cpEditorChipsWrap" role="radiogroup" aria-label="Notification recipients">
          {[
            { value: 'PARENT' as const, label: 'Parents' },
            { value: 'STUDENT' as const, label: 'Students' },
            { value: 'BOTH' as const, label: 'Parents and students' }
          ].map(option => {
            const selected = audienceInvitations === option.value;
            return (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={selected}
                className={`cpEditorChip ${selected ? 'isSelected' : ''}`}
                disabled={disabled}
                onClick={() => setFieldValue('audienceInvitations', option.value)}
              >
                {option.label}
              </button>
            );
          })}
        </div>
        {isInvitationsInvalid ? <div className="cpEditorError">{invitationsError}</div> : null}
      </div>
    </div>
  );
};

ClubProgrammeAudienceFields.displayName = 'ClubProgrammeAudienceFields';
